// apps/share-web/src/song-screen/action-sheet.ts
//
// Лист действий (снизу) и контекстное меню (у точки нажатия) телефонного экрана
// песни. В ДС меню и листа нет (packages/ui-components), поэтому веб делает их
// сам — как version-picker.ts и app-header.ts; кнопка Cancel — ДС-кап
// (chart-buttons.ts). Слой стоит в body: fullscreen-slot.ts умеет делать
// body-детей инертными, когда открыто чтение.
import { textButton } from '../chart/chart-buttons.js';
import { t } from '../ui/i18n.js';
import '../styles/song-screen.css';

export interface SheetItem {
  label: string;
  sub?: string;
  /** Пункт-переключатель: true/false рисует галочку; undefined — обычный пункт. */
  checked?: boolean;
  disabled?: boolean;
  danger?: boolean;
  onSelect: () => void;
}

export interface SheetOpts {
  title?: string;
  items: SheetItem[];
  /** Контекстное меню у точки (clientX/clientY) вместо листа снизу. */
  at?: { x: number; y: number };
  onClose?: () => void;
}

export interface SheetHandle { readonly el: HTMLElement; close(): void; }

const MENU_W = 248;
/**
 * Android Chrome шлёт нативный contextmenu ~500 мс после touchstart — через ~50 мс
 * после того, как наш таймер долгого нажатия (450 мс) уже открыл лист. Событие
 * падает на scrim только что открытого листа; без замка оно его тут же закрыло бы.
 */
const SCRIM_CONTEXT_GUARD_MS = 800;

/** Открытый сейчас лист: на экране он один, второй закрывает первый. */
let active: SheetHandle | null = null;

/** Закрыть открытый лист (если он есть). Зовёт размонтирование микшера. */
export function closeActionSheet(): void {
  active?.close();
}

export function openActionSheet(opts: SheetOpts): SheetHandle {
  // Закрыть предыдущий ДО чтения activeElement: его close() возвращает фокус на
  // кнопку-источник, и новый лист должен запомнить уже её, а не пункт закрытого.
  closeActionSheet();
  const openedAt = performance.now();
  const layer = document.createElement('div');
  layer.className = 'ss-sheet-layer';
  const scrim = document.createElement('div');
  scrim.className = 'ss-scrim';
  const panel = document.createElement('div');
  panel.className = opts.at ? 'ss-menu' : 'ss-sheet';
  panel.setAttribute('role', 'menu');
  if (opts.title) panel.setAttribute('aria-label', opts.title);
  layer.appendChild(scrim);
  layer.appendChild(panel);

  const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let closed = false;

  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    // Escape принадлежит верхней поверхности. Без этого тот же Escape снял бы и
    // петлю (section-handlers.ts слушает document). Слушатель стоит на window в
    // фазе захвата, а не на document: захват идёт window → document, и только так
    // лист обгоняет слушатели document, поставленные раньше него (chart-escape.ts).
    e.stopImmediatePropagation();
    e.preventDefault();
    close();
  };

  function close(): void {
    if (closed) return;
    closed = true;
    if (active === handle) active = null;
    window.removeEventListener('keydown', onKey, true);
    layer.remove();
    opts.onClose?.();
    returnFocus?.focus?.({ preventScroll: true });
  }
  const handle: SheetHandle = { el: panel, close };

  if (opts.title) {
    const h = document.createElement('div');
    h.className = 'ss-sheet-title';
    h.textContent = opts.title;
    panel.appendChild(h);
  }
  for (const it of opts.items) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ss-item' + (it.danger ? ' is-danger' : '');
    b.setAttribute('role', it.checked === undefined ? 'menuitem' : 'menuitemcheckbox');
    if (it.checked !== undefined) b.setAttribute('aria-checked', String(it.checked));
    b.disabled = !!it.disabled;
    const label = document.createElement('span');
    label.className = 'ss-item-label';
    label.textContent = it.label;
    b.appendChild(label);
    if (it.sub) {
      const sub = document.createElement('span');
      sub.className = 'ss-item-sub';
      sub.textContent = it.sub;
      b.appendChild(sub);
    }
    b.addEventListener('click', () => {
      if (b.disabled) return;
      close();
      it.onSelect();
    });
    panel.appendChild(b);
  }
  if (!opts.at) {
    panel.appendChild(textButton({ text: t('songScreen.cancel'), size: 'lg', block: true, cls: 'ss-sheet-cancel', onClick: close }));
  }

  scrim.addEventListener('click', close);
  scrim.addEventListener('contextmenu', (e) => {
    e.preventDefault();                                              // системное меню браузера не нужно никогда
    if (performance.now() - openedAt < SCRIM_CONTEXT_GUARD_MS) return;   // хвост долгого нажатия (см. выше)
    close();
  });
  window.addEventListener('keydown', onKey, true);
  document.body.appendChild(layer);
  active = handle;

  if (opts.at) {
    const vw = window.innerWidth || 360;
    const vh = window.innerHeight || 640;
    const h = panel.offsetHeight || 0;
    const left = Math.min(Math.max(8, opts.at.x - 24), Math.max(8, vw - MENU_W - 8));
    let top = opts.at.y + 12;
    if (h && top + h > vh - 8) top = Math.max(8, opts.at.y - h - 12);
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }
  panel.querySelector<HTMLButtonElement>('.ss-item:not([disabled])')?.focus();
  return handle;
}
